import React from 'react';
import { formatPopupValue } from 'utilities/utils';

// Renders one entry of a map set's "popupContents" array. Each entry names a popupType, and this
// file is the single place that says how a popupType looks:
//
//   popupContents: [
//       { popupType: 'title',      field: 'FullCounty' },
//       { popupType: 'health',     field: 'GeoMean',   name: 'Geometric Mean', unit: ' pCi/L' },
//       { popupType: 'additional', field: 'MaxPreMit', name: 'Highest test value' },
//   ]
//
// Entries render in the order the array lists them, every type is optional, and a type may be
// used as many times as needed. Adding a new type means adding one entry to popupContentTypes
// below -- nothing else in the app needs to change.

// Popup rows are plain divs rather than mui Typography. Typography's styles are injected during
// a real react render -- the standard popup is built with renderToString, where they never arrive.
// Using it therefore made a paginated popup (rendered through a react root) inherit a 16px base
// while every other popup inherited Leaflet's 12px. Plain elements keep both paths identical.

// One label/value row. Every popupType except "title" shares this markup.
const PopupRow = ({
    label,
    showColon,
    valueClassName,
    rowClassName,
    children,
}) => (
    <div className={rowClassName}>
        <div className="popup-data-container">
            {label ? (
                <div className="field-header">
                    {label}
                    {showColon ? ':' : ''}
                </div>
            ) : null}{' '}
            <div className={valueClassName}>{children}</div>
        </div>
    </div>
);

// The title is centred and has no label, so it draws its own markup rather than using PopupRow.
// It is lowercased here because the .popup-title css rule capitalizes it again, which normalizes
// the mix of casings the map services return.
// A long title wraps onto a second line on its own -- the .popup-title css rule reserves the space
// leaflet's close button occupies, so the wrap happens before the text reaches the button.
const renderTitle = ({ content, feature }) => (
    <div className="popup-row">
        <div className="popup-title">
            {feature[content.field]?.toLowerCase()}{' '}
        </div>
    </div>
);

// A comma separated field, shown as a bulleted list.
const renderCommaList = ({ value }) => (
    <ul className="secondary-ul">
        {value.split(',').map((item) => (
            <li key={item} className="secondary-li">
                {item}
            </li>
        ))}
    </ul>
);

// A field holding a url. The href is the raw field value -- it is a url, so it must not go
// through the number formatting the other types use.
const renderLink = ({ content, feature }) => (
    <a target="_blank" rel="noopener noreferrer" href={feature[content.field]}>
        {content.name}
    </a>
);

// How each popupType renders:
//   render         - draws the whole row itself, instead of using PopupRow
//   renderValue    - draws just the value; without it the formatted text is used
//   valueClassName - default style for the value, overridden by an entry's own "className"
//   showColon      - whether the label gets a trailing colon
//   hideLabel      - suppresses the label; for "link", whose "name" is the anchor text
const popupContentTypes = {
    title: { render: renderTitle },
    health: { valueClassName: 'field-value', showColon: true },
    environment: { valueClassName: 'field-value', showColon: true },
    secondary: {
        valueClassName: 'field-value',
        showColon: true,
        renderValue: renderCommaList,
    },
    additional: { valueClassName: 'field-value-align-left', showColon: false },
    link: {
        valueClassName: 'field-value-link',
        showColon: false,
        hideLabel: true,
        renderValue: renderLink,
    },
};

const PopupContent = ({ content, feature }) => {
    const contentType = popupContentTypes[content.popupType];

    // An unrecognised popupType is skipped rather than breaking the whole popup:
    if (!contentType) {
        return null;
    }

    if (contentType.render) {
        return contentType.render({ content, feature });
    }

    const value = formatPopupValue(
        feature[content.field],
        content.unit,
        content.field
    );

    return (
        <PopupRow
            label={contentType.hideLabel ? null : content.name}
            showColon={contentType.showColon}
            valueClassName={content.className || contentType.valueClassName}
            rowClassName={`popup-row popup-${content.popupType}`}
        >
            {contentType.renderValue
                ? contentType.renderValue({ content, feature, value })
                : value}
        </PopupRow>
    );
};

export default PopupContent;
